import { useMemo, useState } from 'react'
import Modal from '../components/Shared/Modal'
import SettingsSection from '../components/Settings/SettingsSection'
import UserAvatar from '../components/Shared/UserAvatar'
import QueueForm from '../components/Queue/QueueForm'
import useQueues from '../hooks/useQueues'
import useTicketCatalogs from '../hooks/useTicketCatalogs'
import { QUEUE_STRATEGIES, typeOwners } from '../utils/queue'
import { TICKET_TYPE_LIST, getTicketType } from '../utils/ticketType'
import { employeeName } from '../utils/employee'

/** Máximo de avatares visibles por cola antes de resumir con "+N". */
const MAX_AVATARS = 5

/**
 * Configuración → Colas de atención. Cada cola atiende uno o varios tipos de
 * registro y asigna automáticamente los tickets nuevos entre sus miembros.
 *
 * @component
 * @returns {JSX.Element}
 */
const TicketQueues = () => {
  const { queues, loading, saving, save, toggleActive, remove } = useQueues()
  const { employees, employeeById } = useTicketCatalogs()
  /** `undefined` = cerrado, `null` = nueva cola, objeto = cola en edición. */
  const [editing, setEditing] = useState(undefined)

  const eligibleEmployees = useMemo(
    () => employees.filter((e) => e.activo !== false),
    [employees]
  )

  // Tipos sin cola activa: sus tickets seguirán llegando sin asignar.
  const uncovered = useMemo(() => {
    const owners = typeOwners(queues.filter((q) => q.active))
    return TICKET_TYPE_LIST.filter((t) => !owners[t.value])
  }, [queues])

  const handleSubmit = async (draft) => {
    const ok = await save(draft, editing?.id ?? null)
    if (ok) setEditing(undefined)
    return ok
  }

  const handleDelete = (queue) => {
    const confirmed = window.confirm(
      `¿Eliminar la cola «${queue.name}»? Los tickets ya asignados conservan su responsable.`
    )
    if (confirmed) remove(queue)
  }

  return (
    <div className="tq">
      <Modal
        isOpen={editing !== undefined}
        onClose={() => setEditing(undefined)}
        title={
          editing ? `Editar cola «${editing.name}»` : 'Nueva cola de atención'
        }
        isLarge
      >
        {editing !== undefined && (
          <QueueForm
            key={editing?.id ?? 'new'}
            queue={editing}
            queues={queues}
            employees={eligibleEmployees}
            saving={saving}
            onSubmit={handleSubmit}
            onCancel={() => setEditing(undefined)}
          />
        )}
      </Modal>

      <SettingsSection
        title="Colas de atención"
        icon="fa-people-arrows"
        description="Cada tipo de registro llega a una cola y se asigna automáticamente a uno de sus miembros."
        actions={
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() => setEditing(null)}
          >
            <i className="fa-solid fa-plus me-2" />
            Nueva cola
          </button>
        }
      >
        {!loading && uncovered.length > 0 && (
          <div className="tq-warning">
            <i className="fa-solid fa-circle-info" />
            <span>
              Sin cola (quedan sin asignar):{' '}
              {uncovered.map((t) => t.label).join(', ')}.
            </span>
          </div>
        )}

        {loading ? (
          <div className="tq-empty">
            <span className="spinner-border spinner-border-sm text-primary" />
          </div>
        ) : queues.length === 0 ? (
          <div className="tq-empty">
            <i className="fa-solid fa-people-arrows d-block fs-4 mb-2 opacity-50" />
            Aún no hay colas. Cree la primera para empezar a asignar tickets
            automáticamente.
          </div>
        ) : (
          <ul className="tq-list">
            {queues.map((queue) => {
              const strategy = QUEUE_STRATEGIES[queue.strategy]
              const members = queue.memberIds.map(
                (id) => employeeById[id] || { id }
              )
              return (
                <li
                  key={queue.id}
                  className={`tq-item ${queue.active ? '' : 'is-inactive'}`}
                >
                  <span
                    className="tq-dot"
                    style={{ background: queue.color }}
                  />

                  <div className="tq-main">
                    <div className="tq-name">
                      {queue.name}
                      {!queue.active && (
                        <span className="tq-badge">Inactiva</span>
                      )}
                    </div>
                    {queue.description && (
                      <div className="tq-desc">{queue.description}</div>
                    )}
                    <div className="tq-types">
                      {queue.types.map((value) => {
                        const type = getTicketType(value)
                        return (
                          <span
                            key={value}
                            className="tq-type"
                            style={{
                              color: type.color,
                              background: `${type.color}14`,
                            }}
                          >
                            <i className={`fa-solid ${type.icon}`} />
                            {type.label}
                          </span>
                        )
                      })}
                    </div>
                  </div>

                  <div className="tq-meta">
                    <span className="tq-strategy" title={strategy?.hint}>
                      <i className={`fa-solid ${strategy?.icon}`} />
                      {strategy?.label}
                    </span>
                    <span
                      className="tq-avatars"
                      title={members.map(employeeName).join(', ')}
                    >
                      {members.slice(0, MAX_AVATARS).map((m) => (
                        <UserAvatar key={m.id} employee={m} size={24} />
                      ))}
                      {members.length > MAX_AVATARS && (
                        <span className="tq-more">
                          +{members.length - MAX_AVATARS}
                        </span>
                      )}
                      {members.length === 0 && (
                        <span className="tq-desc">Sin miembros</span>
                      )}
                    </span>
                  </div>

                  <div className="tq-actions">
                    <label
                      className="form-check form-switch m-0"
                      title="Activa"
                    >
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={queue.active}
                        onChange={() => toggleActive(queue)}
                        aria-label={`Activar cola ${queue.name}`}
                      />
                    </label>
                    <button
                      type="button"
                      className="tq-icon-btn"
                      title="Editar"
                      onClick={() => setEditing(queue)}
                    >
                      <i className="fa-solid fa-pen" />
                    </button>
                    <button
                      type="button"
                      className="tq-icon-btn is-danger"
                      title="Eliminar"
                      onClick={() => handleDelete(queue)}
                    >
                      <i className="fa-solid fa-trash" />
                    </button>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </SettingsSection>

      <style>{`
        .tq-warning {
          display: flex; gap: 8px; align-items: baseline; padding: 8px 12px;
          border-radius: 8px; font-size: 0.8rem; color: var(--gm-text-muted); background: var(--gm-surface-2);
        }
        .tq-warning i { color: var(--gm-primary); }
        .tq-empty { padding: 32px 12px; text-align: center; font-size: 0.85rem; color: var(--gm-text-faint); }
        .tq-list { list-style: none; margin: 0; padding: 0; border: 1px solid var(--gm-border); border-radius: 10px; overflow: hidden; }
        .tq-item {
          display: flex; align-items: center; gap: 12px; padding: 10px 12px;
          border-bottom: 1px solid var(--gm-border);
        }
        .tq-item:last-child { border-bottom: none; }
        .tq-item.is-inactive { opacity: 0.6; }
        .tq-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
        .tq-main { flex: 1; min-width: 0; }
        .tq-name { font-weight: 600; font-size: 0.88rem; color: var(--gm-text); display: flex; align-items: center; gap: 8px; }
        .tq-badge { font-size: 0.65rem; font-weight: 600; padding: 1px 7px; border-radius: 99px; background: var(--gm-surface-2); color: var(--gm-text-muted); }
        .tq-desc { font-size: 0.75rem; color: var(--gm-text-muted); }
        .tq-types { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 4px; }
        .tq-type { display: inline-flex; align-items: center; gap: 4px; padding: 1px 8px; border-radius: 99px; font-size: 0.7rem; font-weight: 600; }
        .tq-type i { font-size: 0.62rem; }
        .tq-meta { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; flex-shrink: 0; }
        .tq-strategy { font-size: 0.72rem; color: var(--gm-text-muted); display: inline-flex; gap: 5px; align-items: center; }
        .tq-avatars { display: inline-flex; align-items: center; }
        .tq-avatars > * + * { margin-left: -6px; }
        .tq-avatars .user-avatar { box-shadow: 0 0 0 2px var(--gm-surface); }
        .tq-more { font-size: 0.7rem; font-weight: 600; color: var(--gm-text-muted); margin-left: 4px !important; }
        .tq-actions { display: flex; align-items: center; gap: 4px; flex-shrink: 0; }
        .tq-icon-btn {
          width: 28px; height: 28px; display: inline-flex; align-items: center; justify-content: center;
          border: none; border-radius: 6px; background: transparent; color: var(--gm-text-muted); font-size: 0.78rem;
        }
        .tq-icon-btn:hover { background: var(--gm-surface-2); color: var(--gm-text); }
        .tq-icon-btn.is-danger:hover { color: var(--gm-danger); }
        @media (max-width: 767px) {
          .tq-item { flex-wrap: wrap; }
          .tq-meta { align-items: flex-start; flex-direction: row; gap: 12px; }
        }
      `}</style>
    </div>
  )
}

export default TicketQueues
